import React from 'react';
import { BarChart3 } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { formatCurrency, formatNumber } from '../../../utils/analysis.utils';
import { useTranslation } from 'react-i18next';

interface ValueCaptureCurveProps {
  analysis: any;
  valueCurveChartData: any;
  valueCurveOptions: any;
}

export const ValueCaptureCurve: React.FC<ValueCaptureCurveProps> = ({
  analysis,
  valueCurveChartData,
  valueCurveOptions
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <h3 className="text-base font-semibold text-black flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-brand-primary" />
          </div>
          {t('optimizationAnalysis.theoretical.valueCaptureCurve.title')}
        </h3>
      </div>
      <div className="p-6">
        <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-700">
            <strong>{t('optimizationAnalysis.theoretical.valueCaptureCurve.strategyLabel')}</strong> {t('optimizationAnalysis.theoretical.valueCaptureCurve.strategyText')}
          </p>
        </div>

        <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-600">{t('optimizationAnalysis.theoretical.valueCaptureCurve.hoursNeeded')}</p>
              <p className="text-lg font-bold text-gray-900">
                {formatNumber(analysis?.theoretical?.hoursNeeded || 0)}
              </p>
              <p className="text-xs text-gray-500">{t('optimizationAnalysis.theoretical.valueCaptureCurve.forAllConsumption')}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">{t('optimizationAnalysis.theoretical.valueCaptureCurve.cheapHoursAvailable')}</p>
              <p className="text-lg font-bold text-gray-900">
                {formatNumber(analysis?.hoursBelowRetail || 0)}
              </p>
              <p className="text-xs text-gray-500">{t('optimizationAnalysis.theoretical.valueCaptureCurve.belowRetailPrice')}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">{t('optimizationAnalysis.theoretical.valueCaptureCurve.maxSavings')}</p>
              <p className="text-lg font-bold text-brand-primary">
                {formatCurrency(analysis?.theoretical?.maximum || 0)}
              </p>
              <p className="text-xs text-gray-500">{t('optimizationAnalysis.theoretical.valueCaptureCurve.theoreticalMaximum')}</p>
            </div>
          </div>
        </div>

        <div className="h-64">
          {analysis?.valueCaptureCurve?.length > 0 ? (
            <Line data={valueCurveChartData} options={valueCurveOptions} />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-sm">{t('optimizationAnalysis.theoretical.valueCaptureCurve.noDataAvailable')}</p>
            </div>
          )}
        </div>
        <div className="mt-4 bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-700 font-semibold mb-2">
            {t('optimizationAnalysis.theoretical.valueCaptureCurve.howToReadTitle')}
          </p>
          <ul className="text-xs text-gray-600 space-y-1">
            <li dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.valueCaptureCurve.xAxis') }} />
            <li dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.valueCaptureCurve.yAxis') }} />
            <li dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.valueCaptureCurve.curveRises') }} />
            <li dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.valueCaptureCurve.curveFlattens') }} />
            <li dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.valueCaptureCurve.keyInsight', { hours: formatNumber(analysis?.theoretical?.hoursNeeded || 0), mwh: formatNumber(analysis?.totalConsumption / 1000 || 0, 0) }) }} />
          </ul>

          <div className="mt-2 p-2 bg-gray-100 rounded">
            <p className="text-xs text-gray-700">
              <strong>{t('optimizationAnalysis.theoretical.valueCaptureCurve.exampleTitle')}</strong> {t('optimizationAnalysis.theoretical.valueCaptureCurve.exampleText')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};